// ─────────────────────────────────────────────────────────────────────────────
// Individual validators  (return boolean)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Validates an email address.
 * @param {string} email
 * @returns {boolean}
 */
export function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
}

/**
 * Validates a 10-digit Indian mobile number (must start with 6-9).
 * @param {string} phone
 * @returns {boolean}
 */
export function validateIndianMobile(phone) {
  return /^[6-9]\d{9}$/.test(phone.trim());
}

/**
 * Validates an Indian PIN code (6-digit, cannot start with 0).
 * @param {string} pin
 * @returns {boolean}
 */
export function validateIndianPinCode(pin) {
  return /^[1-9][0-9]{5}$/.test(pin.trim());
}

/**
 * Validates an IMEI number using the Luhn algorithm (exactly 15 digits).
 * @param {string} imei
 * @returns {boolean}
 */
export function validateIMEI(imei) {
  const cleaned = imei.replace(/\D/g, "");
  if (cleaned.length !== 15) return false;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let digit = parseInt(cleaned[i], 10);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

/**
 * Validates a mobile phone number (international and common formats).
 * @param {string} phone
 * @returns {boolean}
 */
export function validateMobileNumber(phone) {
  return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{3,4}[-\s.]?[0-9]{3,6}$/.test(
    phone.trim(),
  );
}

/**
 * Validates a US ZIP code (5-digit or ZIP+4).
 * @param {string} zip
 * @returns {boolean}
 */
export function validateUSZipCode(zip) {
  return /^\d{5}(-\d{4})?$/.test(zip.trim());
}

/**
 * Validates a UK postcode.
 * @param {string} postcode
 * @returns {boolean}
 */
export function validateUKPostcode(postcode) {
  return /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(postcode.trim());
}

/**
 * Validates a Canadian postal code.
 * @param {string} postalCode
 * @returns {boolean}
 */
export function validateCanadianPostalCode(postalCode) {
  return /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(postalCode.trim());
}

// ─────────────────────────────────────────────────────────────────────────────
// validateField  — real-time single-field validator used by the form
// Returns an error string, or '' if valid.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {'fullName'|'email'|'mobile'|'zip'|'brand'|'imei'|'deviceValue'} fieldName
 * @param {string|number} value
 * @returns {string}  empty string = no error
 */
export function validateField(fieldName, value) {
  const v = String(value ?? "");

  switch (fieldName) {
    case "fullName":
      if (!v.trim()) return "Full name is required.";
      if (v.trim().length < 2) return "Name must be at least 2 characters.";
      return "";

    case "email":
      if (!v.trim()) return "Email address is required.";
      if (!validateEmail(v)) return "Enter a valid email address.";
      return "";

    case "mobile":
      if (!v.trim()) return "Mobile number is required.";
      if (!validateIndianMobile(v))
        return "Enter a valid 10-digit mobile number (starts with 6–9).";
      return "";

    case "zip":
      if (!v.trim()) return "PIN code is required.";
      if (!validateIndianPinCode(v))
        return "Enter a valid 6-digit PIN code (cannot start with 0).";
      return "";

    case "state":
      if (!v.trim()) return "Please select a state.";
      return "";

    case "city":
      if (!v.trim()) return "Please select a city / district.";
      return "";

    case "brand":
      if (!v.trim()) return "Please select a device brand.";
      return "";

    // case "imei":
    //   if (!v.trim()) return "IMEI number is required.";
    //   if (!/^\d+$/.test(v.replace(/\s/g, "")))
    //     return "IMEI must contain digits only.";
    //   if (!validateIMEI(v))
    //     return "Enter a valid 15-digit IMEI number (Luhn check failed).";
    //   return "";

    case "deviceValue":
      if (!v || Number(v) <= 0) return "Enter a valid device invoice value.";
      if (Number(v) < 1000) return "Device value must be at least ₹1,000.";
      // if (Number(v) > 5000000) return "Device value cannot exceed ₹5,000,000.";
      return "";

    default:
      return "";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// validateFullForm  — run all fields at once, returns errors object
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {Object} form        — { fullName, email, mobile, zip, brand, imei, deviceValue }
 * @param {Object} sel         — { os, dev, plan, benefit }
 * @param {File|null}   invoiceFile
 * @param {Blob|string|null} recordingFile
 * @param {boolean} termsChecked
 * @returns {Object}  { fieldName: errorString, … }  — empty object = all valid
 */
export function validateFullForm(
  form,
  sel,
  invoiceFile,
  recordingFile,
  termsChecked,
) {
  const errors = {};

  // Text fields
  [
    "fullName",
    "email",
    "mobile",
    "zip",
    "state",
    "city",
    "brand",
    "imei",
    "deviceValue",
  ].forEach((field) => {
    const err = validateField(field, form[field]);
    if (err) errors[field] = err;
  });

  // Plan selection
  if (!sel.os || !sel.dev || !sel.plan || !sel.benefit) {
    errors.plan = "Please complete all 4 plan selection steps.";
  }

  // Documents
  if (!invoiceFile) {
    errors.invoice = "Please upload your device invoice (PDF or image).";
  }
  if (!recordingFile) {
    errors.recording = "Please provide your IMEI screen recording.";
  }

  // Terms
  if (!termsChecked) {
    errors.terms = "You must agree to the Terms of Service to continue.";
  }

  return errors;
}

// ─────────────────────────────────────────────────────────────────────────────
// Generic validate()  — kept for backward compatibility
// ─────────────────────────────────────────────────────────────────────────────
export function validate(value, type) {
  if (!value || typeof value !== "string") {
    return { valid: false, message: "Value must be a non-empty string." };
  }
  const map = {
    mobile: [
      validateMobileNumber,
      "Valid mobile number.",
      "Invalid mobile number.",
    ],
    mobile_india: [
      validateIndianMobile,
      "Valid Indian mobile number.",
      "Invalid Indian mobile. Must be 10 digits starting with 6–9.",
    ],
    imei: [
      validateIMEI,
      "Valid IMEI number.",
      "Invalid IMEI. Must be 15 digits and pass Luhn check.",
    ],
    zip_us: [
      validateUSZipCode,
      "Valid US ZIP code.",
      "Invalid US ZIP code. Use 5-digit or ZIP+4 format.",
    ],
    pin_india: [
      validateIndianPinCode,
      "Valid Indian PIN code.",
      "Invalid Indian PIN. Must be 6 digits, not starting with 0.",
    ],
    postcode_uk: [
      validateUKPostcode,
      "Valid UK postcode.",
      "Invalid UK postcode.",
    ],
    postalcode_ca: [
      validateCanadianPostalCode,
      "Valid Canadian postal code.",
      "Invalid Canadian postal code.",
    ],
  };
  const entry = map[type];
  if (!entry)
    return { valid: false, message: `Unknown validation type: "${type}".` };
  const [fn, okMsg, failMsg] = entry;
  return fn(value)
    ? { valid: true, message: okMsg }
    : { valid: false, message: failMsg };
}
